<template>
  <div class="payment-step-container">
    <h3 class="payment-title mb-3">How do you like to pay?</h3>

    <div class="row">
      <!-- Payment Options Column -->
      <div class="col-lg-7 col-md-12 mb-4 mb-lg-0">
        <div class="payment-options-card">
          <div
            class="section-title-wrapper d-flex justify-content-between align-items-center"
          >
            <span class="section-title">Available Balance</span>
            <span class="quickest-transaction-badge">Quickest Transaction</span>
          </div>
          <div
            class="payment-option available-balance"
            :class="{
              selected: selectedPaymentMethod === 'balance',
              'insufficient-balance-option':
                selectedPaymentMethod === 'balance' &&
                !hasSufficientBalance &&
                !isLoadingBalance,
              'disabled-option':
                isLoadingBalance && selectedPaymentMethod === 'balance',
            }"
            @click="!isLoadingBalance && selectPaymentMethod('balance')"
          >
            <input
              type="radio"
              id="payWithBalance"
              name="paymentMethod"
              value="balance"
              v-model="selectedPaymentMethod"
              class="form-check-input"
              :disabled="isLoadingBalance"
            />
            <label for="payWithBalance" class="payment-label">
              <span v-if="isLoadingBalance">Loading balance...</span>
              <span v-else>
                IDR {{ formatNumber(localAvailableBalance) }}
                <span
                  v-if="
                    !hasSufficientBalance && selectedPaymentMethod === 'balance'
                  "
                  class="insufficient-balance-text ms-2"
                >
                  Insufficient Balance
                </span>
              </span>
            </label>
          </div>

          <span class="section-title mt-4">Transfer Virtual Account</span>
          <div
            v-for="va in virtualAccounts"
            :key="va.id"
            class="payment-option virtual-account"
            :class="{ selected: selectedPaymentMethod === va.id }"
            @click="selectPaymentMethod(va.id)"
          >
            <input
              type="radio"
              :id="'va-' + va.id"
              name="paymentMethod"
              :value="va.id"
              v-model="selectedPaymentMethod"
              class="form-check-input"
            />
            <label
              :for="'va-' + va.id"
              class="payment-label d-flex align-items-center"
            >
              <img :src="va.logo" :alt="va.name" class="va-logo me-2" />
              {{ va.name }}
            </label>
          </div>
        </div>
      </div>

      <!-- Transaction Summary Column -->
      <div class="col-lg-5 col-md-12">
        <div class="transaction-summary-card">
          <h5 class="summary-card-title">Transaction Summary</h5>
          <div class="summary-item">
            <span>Entries</span>
            <span class="summary-value">{{
              props.batchSummary ? props.batchSummary.total_recipient : 0
            }}</span>
          </div>
          <div class="summary-item">
            <span>Total Transfer Amount</span>
            <span class="summary-value"
              >IDR
              {{
                formatNumber(
                  props.batchSummary ? props.batchSummary.total_amount : 0
                )
              }}</span
            >
          </div>
          <div class="summary-item">
            <span>Admin Fee</span>
            <span class="summary-value"
              >IDR
              {{
                formatNumber(
                  props.batchSummary ? props.batchSummary.total_admin_fee : 0
                )
              }}</span
            >
          </div>
          <hr class="summary-divider" />
          <div class="summary-item total-amount">
            <span>Total</span>
            <span class="summary-value"
              >IDR
              {{
                formatNumber(
                  props.batchSummary ? props.batchSummary.grand_total_amount : 0
                )
              }}</span
            >
          </div>
          <ArgonButton
            color="success"
            full-width
            size="lg"
            @click="handleSubmitPayment"
            class="mt-4 pay-button"
          >
            Pay IDR
            {{
              formatNumber(
                props.batchSummary ? props.batchSummary.grand_total_amount : 0
              )
            }}
          </ArgonButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import ArgonButton from "@/components/ArgonButton.vue";
import { getBalance, sendOtp } from "@/api/transfer";
import Swal from "sweetalert2";

const router = useRouter();
const store = useStore();
const emailFromStore = ref(
  store.state.auth.email || localStorage.getItem("merchant_email")
);
const maskedEmail = computed(() => {
  const [localPart, domain] = emailFromStore.value.split("@");
  const maskedLocal = localPart[0] + "***";
  const [domainName, domainExt] = domain.split(".");
  const maskedDomain = domainName[0] + "**" + domainName.slice(-1);
  return `${maskedLocal}@${maskedDomain}.${domainExt}`;
});

// Define props to receive data from the parent component (Transfer_baru.vue)
const props = defineProps({
  batchSummary: {
    type: Object,
    default: () => ({
      total_recipient: 0,
      total_amount: "0",
      total_admin_fee: "0",
      grand_total_amount: "0",
    }),
  },
  // availableBalance prop will now be managed internally by this component
  // availableBalance: {
  //   type: Number,
  //   default: 0
  // },
  batchUUID: {
    type: String,
    default: null,
  },
});

const localAvailableBalance = ref(0); // <<< Local state for balance
const isLoadingBalance = ref(true);

// eslint-disable-next-line no-unused-vars
const emit = defineEmits(["back", "submit-payment"]);

const selectedPaymentMethod = ref("balance"); // Default to 'balance'

const virtualAccounts = ref([
  {
    id: 1,
    name: "Permata Virtual Account",
    logo: require("@/assets/img/logos/bank-permata.png"),
  },
  // {
  //   id: 2,
  //   name: "BNI Virtual Account",
  //   logo: require("@/assets/img/logos/bank-bni.png"),
  // },
  // {
  //   id: 3,
  //   name: "BRI Virtual Account",
  //   logo: require("@/assets/img/logos/bank-bri.png"),
  // },
  // {
  //   id: 4,
  //   name: "Mandiri Virtual Account",
  //   logo: require("@/assets/img/logos/bank-mandiri.png"),
  // },
  // {
  //   id: 5,
  //   name: "BCA Virtual Account",
  //   logo: require("@/assets/img/logos/bank-bca.png"),
  // },
]);

const formatNumber = (value) => {
  if (value === null || value === undefined) return "0";
  const num = Number(value);
  if (isNaN(num)) return value; // Return original if not a number (e.g. for error messages)
  return num.toLocaleString("id-ID"); // Using Indonesian locale for formatting like 120.000.000
};

const selectPaymentMethod = (methodId) => {
  selectedPaymentMethod.value = methodId;
};

onMounted(async () => {
  isLoadingBalance.value = true;
  try {
    const balanceResponse = await getBalance();
    if (
      balanceResponse &&
      balanceResponse.status === "success" &&
      balanceResponse.data &&
      balanceResponse.data.merchant
    ) {
      localAvailableBalance.value =
        normalizeIndonesianNumber(balanceResponse.data.merchant.balance) || 0;
    } else {
      console.error(
        "PaymentStep: Failed to fetch balance:",
        balanceResponse?.message
      );
      localAvailableBalance.value = 0;
    }
  } catch (error) {
    console.error("PaymentStep: Error fetching balance:", error);
    localAvailableBalance.value = 0;
  } finally {
    isLoadingBalance.value = false;
  }
});

const normalizeIndonesianNumber = (value) => {
  if (typeof value === "string") {
    // Remove dots (thousand separator) and convert comma (decimal) if needed
    value = value.replace(/\./g, "").replace(",", ".");
  }
  return Number(value);
};

const grandTotalAmount = computed(() => {
  return parseFloat(props.batchSummary?.grand_total_amount || 0);
});

const hasSufficientBalance = computed(() => {
  if (isLoadingBalance.value) return true; // Assume sufficient while loading to prevent premature error state
  const balance = normalizeIndonesianNumber(localAvailableBalance.value);
  return balance >= grandTotalAmount.value;
});

const isSendingOtp = ref(false); // For loading state on Pay button

const handleSubmitPayment = async () => {
  if (!selectedPaymentMethod.value) {
    Swal.fire("Error", "Please select a payment method.", "error");
    return;
  }

  if (
    selectedPaymentMethod.value === "balance" &&
    !hasSufficientBalance.value
  ) {
    Swal.fire(
      "Error",
      "Insufficient balance to make this payment. Please choose another payment method or top up your balance.",
      "error"
    );
    return;
  }

  if (!props.batchUUID) {
    Swal.fire(
      "Error",
      "Batch ID is missing. Cannot proceed with payment.",
      "error"
    );
    return;
  }

  isSendingOtp.value = true;
  const formData = new FormData();
  formData.append("batch_id", props.batchUUID);

  let paymentMethodValue = "";
  let bankIdentifier = null;

  if (selectedPaymentMethod.value === "balance") {
    paymentMethodValue = "balance";
  } else {
    paymentMethodValue = "virtual_account";
    const selectedVA = virtualAccounts.value.find(
      (va) => va.id === selectedPaymentMethod.value
    );
    if (selectedVA) {
      bankIdentifier = selectedVA.id;
      formData.append("bank_id", bankIdentifier);
    } else if (selectedPaymentMethod.value !== "balance") {
      Swal.fire(
        "Error",
        "Selected virtual account details not found.",
        "error"
      );
      isSendingOtp.value = false;
      return;
    }
  }
  formData.append("method", paymentMethodValue);

  try {
    const otpResponse = await sendOtp(formData);

    if (otpResponse && otpResponse.status === "success") {
      // Navigate to OTP verification page using history.state
      router.push({
        name: "OtpVerification",
        // Remove the query block
        // query: { ... }
        state: {
          // Pass data via history.state
          maskedEmail:
            otpResponse.data?.masked_email || maskedEmail.value || "",
          batchUUID: props.batchUUID,
          paymentMethod: paymentMethodValue,
          bankId:
            paymentMethodValue === "virtual_account"
              ? bankIdentifier
              : undefined,
        },
      });
    } else {
      Swal.fire(
        "Error",
        otpResponse.message ||
          "Failed to initiate payment and send OTP. Please try again.",
        "error"
      );
    }
  } catch (error) {
    console.error("Error during handleSubmitPayment (sendOtp):", error);
    Swal.fire(
      "Error",
      "An error occurred while trying to initiate payment.",
      "error"
    );
  } finally {
    isSendingOtp.value = false;
  }
};

// The original emit('submit-payment') might now be handled after OTP verification
// emit('submit-payment', {
//   paymentMethod: selectedPaymentMethod.value,
//   transactionDetails: props.batchSummary,
//   batchUUID: props.batchUUID
// });
</script>

<style scoped>
.payment-step-container {
  padding: 20px;
  background-color: #f8f9fa; /* Light background for the whole step */
}

.back-button {
  font-size: 0.9rem;
  color: #5e72e4; /* Argon primary */
  text-decoration: none;
}
.back-button:hover {
  text-decoration: underline;
}

.payment-title {
  font-size: 1.75rem; /* "How do you like to pay?" */
  font-weight: 600;
  color: #344767;
}

.payment-options-card,
.transaction-summary-card {
  background-color: #fff;
  border-radius: 0.75rem;
  padding: 25px;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.section-title-wrapper {
  margin-bottom: 10px;
}

.section-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #344767;
  display: block;
  margin-bottom: 8px;
}

.quickest-transaction-badge {
  background-color: #e9ecef; /* Light blue/greyish */
  color: #0d6efd; /* Bootstrap info blue */
  padding: 4px 8px;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 500;
}

.payment-option {
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  padding: 15px;
  margin-bottom: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}

.payment-option:hover {
  border-color: #5e72e4;
}

.payment-option.selected {
  border-color: #5e72e4; /* Argon primary */
  box-shadow: 0 0 0 0.2rem rgba(94, 114, 228, 0.25);
  background-color: #f0f3ff; /* Light primary background */
}

.payment-option .form-check-input {
  margin-right: 12px;
  width: 1.2em;
  height: 1.2em;
  margin-top: 0.1em; /* Align better with text */
}

.payment-label {
  font-weight: 500;
  color: #344767;
  margin-bottom: 0; /* Override default label margin */
  flex-grow: 1;
}

.available-balance .payment-label {
  font-size: 1.1rem;
  font-weight: bold;
}

.va-logo {
  height: 24px; /* Adjust as needed */
  width: auto;
}

.summary-card-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #344767;
  margin-bottom: 20px;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  font-size: 0.9rem;
  color: #6c757d; /* Muted text */
  margin-bottom: 12px;
}

.summary-item .summary-value {
  font-weight: 600;
  color: #344767;
}

.summary-divider {
  margin-top: 15px;
  margin-bottom: 15px;
  border-color: #e9ecef;
}

.total-amount span {
  font-size: 1.1rem;
  font-weight: bold;
  color: #344767;
}

.total-amount .summary-value {
  font-size: 1.2rem; /* Make total value slightly larger */
}

.pay-button {
  font-weight: 600;
  font-size: 1rem;
}

.payment-option.insufficient-balance-option {
  border-color: #fb6340; /* Argon danger color for border */
  background-color: #fdece8; /* Lighter shade for danger background */
}

.payment-option.insufficient-balance-option .payment-label,
.insufficient-balance-text {
  color: #fb6340; /* Argon danger color for text */
}

.payment-option.disabled-option {
  opacity: 0.7;
  cursor: not-allowed;
}

.insufficient-balance-text {
  font-weight: normal;
  font-size: 0.85em;
}
</style>
